import classnames from 'classnames';
import type { FunctionComponent, ReactNode } from 'react';
import { useContext } from 'react';
import {
  FormattedMessage,
  FormattedNumber,
  useIntl,
  type IntlShape,
} from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { Button } from '@trello/nachos/button';

import { PlanComparisonContext } from './PlanComparisonContext';

import styles from './PlanComparisonTotals.less';

export interface TotalsCellProps {
  monthlyPrice?: number;
  annualPrice?: number;
  isMonthly: boolean;
  intl: IntlShape;
  monthlyCollaborators?: number;
  annualCollaborators?: number;
}

export const getTotalsText = ({
  isMonthly,
  annualPrice = 0,
  monthlyPrice = 0,
  intl,
}: TotalsCellProps) => {
  const monthlyRatePerYear = monthlyPrice ? monthlyPrice * 12 : 0;

  const formatUsdPrice = (amount: number) => {
    return intl.formatMessage(
      {
        id: 'templates.billing_page_one.amount-usd',
        defaultMessage: '${amount} USD',
        description: 'Format for displaying price in USD currency',
      },
      {
        amount: (
          <FormattedNumber
            value={amount}
            style="currency"
            currency="USD"
            minimumFractionDigits={0}
            maximumFractionDigits={0}
          />
        ).props.value.toString(),
      },
    );
  };

  return isMonthly ? (
    <p className={classnames(styles.text, styles.total)}>
      <FormattedMessage
        id="templates.billing_page_one.monthly-total-text"
        defaultMessage="{monthlyPrice} total per month, totaling {annualPrice} annually"
        description="Text showing monthly price and annual total when billed monthly"
        values={{
          monthlyPrice: (
            <span key={monthlyPrice}>
              <b className={styles.price}>{formatUsdPrice(monthlyPrice)}</b>
              <br />
            </span>
          ),
          annualPrice: formatUsdPrice(monthlyRatePerYear),
        }}
      />
    </p>
  ) : (
    <p className={classnames(styles.text, styles.total)}>
      <FormattedMessage
        id="templates.billing_page_one.annual-total-text"
        defaultMessage="{annualPrice} paid annually"
        description="Text showing total price when billed annually"
        values={{
          annualPrice: (
            <span key={annualPrice}>
              <b className={styles.price}>{formatUsdPrice(annualPrice)}</b>
              <br />
            </span>
          ),
        }}
      />
    </p>
  );
};

const TotalsCell: FunctionComponent<TotalsCellProps> = ({
  monthlyPrice,
  annualPrice,
  isMonthly,
  intl,
}) => {
  const totalsText = getTotalsText({
    monthlyPrice,
    annualPrice,
    isMonthly,
    intl,
  });
  return (
    <td className={classnames(styles.cell, styles.cellTotal)}>{totalsText}</td>
  );
};

interface OwnProps {
  teamName: ReactNode;
  isMonthly: boolean;
  numOfMembers?: number;
  teamId: string;
}

export const PlanComparisonTotals: FunctionComponent<OwnProps> = ({
  teamName,
  isMonthly,
  numOfMembers = 1,
  teamId,
}) => {
  const intl = useIntl();

  const { billingQuote, variation } = useContext(PlanComparisonContext);

  const standardMonthly = billingQuote?.standardPrices?.monthly;
  const standardAnnual = billingQuote?.standardPrices?.annual;
  const businessMonthly = billingQuote?.businessPrices?.monthly;
  const businessAnnual = billingQuote?.businessPrices?.annual;

  const onClickContactUs = () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'contactUsButton',
      source:
        variation === 'billing-page'
          ? 'planComparisonSection'
          : 'planComparisonModal',
      containers: {
        organization: {
          id: teamId,
        },
      },
    });
  };

  return (
    <tr>
      <td key="empty-cell" className={styles.noCell}>
        {}
      </td>
      <td
        key="header-cell"
        className={classnames(styles.cell, styles.cellHeader)}
      >
        <p className={styles.text}>
          <FormattedMessage
            id="templates.billing_page_one.plan-comparison-totals-row-header"
            defaultMessage="For all members and billable guests in {teamName}, you'd pay:"
            description="Header text showing what the total cost would be for all team members"
            values={{ teamName }}
          />
        </p>
      </td>
      <TotalsCell
        key="standard-totals"
        monthlyPrice={standardMonthly?.subTotal}
        annualPrice={standardAnnual?.subTotal}
        isMonthly={isMonthly}
        intl={intl}
      />
      <TotalsCell
        key="business-totals"
        monthlyPrice={businessMonthly?.subTotal}
        annualPrice={businessAnnual?.subTotal}
        isMonthly={isMonthly}
        intl={intl}
      />
      <td className={classnames(styles.cell, styles.contactBtn)}>
        <Button
          onClick={onClickContactUs}
          key="enterprise-contact-us"
          linkTarget="_blank"
          href={'/enterprise#contact'}
        >
          <FormattedMessage
            id="templates.billing_page_one.contact-sales-repackaging-gtm"
            defaultMessage="Contact sales"
            description="Button text for contacting sales team about Enterprise plan"
          />
        </Button>
      </td>
    </tr>
  );
};
